// AGC: Core game engine for Origami Battles battle system
// AGC: Implements exact damage calculations and RNG mechanics per specification

export type OrigamiChoice = 'ROCK' | 'PAPER' | 'SCISSORS'
export type ElementChoice = 'FIRE' | 'WATER' | 'ICE' | 'WIND'
export type BuffType = 'SHIELD' | 'PIERCE' | 'HASTE' | 'FROST'
export type RarityType = 'DIAMOND' | 'GOLD' | 'SILVER' | 'BRONZE' | 'NONE'

export interface Player {
  id: string
  level: number
  ep: number // Energy Points (health)
  experience: number // Experience points
  choice?: {
    origami: OrigamiChoice
    element: ElementChoice
  }
  buffs: BuffType[]
  usedFrost?: boolean // AGC: Track if Frost has been used (one-time use only)
  rarity?: RarityType
}

export interface PlayerStats {
  level: number
  experience: number
  experienceToNext: number
  totalExperience: number
  attack: number
  defense: number
  health: number
}

export interface DamageBreakdown {
  baseDamage: number
  baseName: string // "WIN", "DRAW", or "LOSE"
  levelBonus: number
  elementBonus: number
  elementName: string // "ADVANTAGE", "NEUTRAL", or "DISADVANTAGE"
  rarityBonus: number
  rarityName: string // "DIAMOND", "GOLD", etc.
  hasteBonus: number
  rawDamage: number
  defense: number
  pierceReduction?: number
  afterDefense: number
  frostReduction?: number
  shieldReduction?: number
  finalDamage: number
  activatedBuffs?: BuffType[]
}

export interface BattleResult {
  playerA_damage: number
  playerB_damage: number
  playerA_ep: number
  playerB_ep: number
  winner?: 'A' | 'B' | 'DRAW'
  rng_seed: string
  playerA_rarity?: RarityType
  playerB_rarity?: RarityType
  playerA_buff?: BuffType | null
  playerB_buff?: BuffType | null
  playerA_buffs?: BuffType[]
  playerB_buffs?: BuffType[]
  playerA_breakdown?: DamageBreakdown
  playerB_breakdown?: DamageBreakdown
}

export class GameEngine {
  // AGC: RNG constants based on specification
  private static readonly RARITY_THRESHOLDS = {
    DIAMOND: 1000,
    GOLD: 11000,
    SILVER: 61000,
    BRONZE: 161000
  }

  // AGC: New simplified buff system - no longer using ppm rates
  // AGC: Rates are calculated dynamically based on position and turn

  // AGC: Damage base values per origami choice outcome
  private static readonly BASE_DAMAGE = {
    WIN: 30,
    DRAW: 15,
    LOSE: 10
  }

  // AGC: Element advantage/disadvantage modifiers
  private static readonly ELEMENT_MODIFIERS = {
    ADVANTAGE: 10, // +10 damage
    NEUTRAL: 0,    // +0 damage
    DISADVANTAGE: -5 // -5 damage
  }

  // AGC: Rarity damage bonuses
  private static readonly RARITY_BONUSES = {
    DIAMOND: 50,
    GOLD: 25,
    SILVER: 10,
    BRONZE: 5,
    NONE: 0
  }

  /**
   * AGC: Determines origami battle outcome (Rock-Paper-Scissors logic)
   * AGC: Returns 'WIN', 'DRAW', or 'LOSE' from player A's perspective
   */
  static determineOrigamiOutcome(playerA: OrigamiChoice, playerB: OrigamiChoice): 'WIN' | 'DRAW' | 'LOSE' {
    if (playerA === playerB) return 'DRAW'
    
    const winConditions = {
      ROCK: 'SCISSORS',
      PAPER: 'ROCK',
      SCISSORS: 'PAPER'
    }
    
    return winConditions[playerA] === playerB ? 'WIN' : 'LOSE'
  }

  /**
   * AGC: Determines element advantage based on Water > Fire > Ice > Wind > Water cycle
   * AGC: Non-adjacent elements result in neutral (draw) damage
   * AGC: Returns advantage modifier for damage calculation
   */
  static determineElementAdvantage(attacker: ElementChoice, defender: ElementChoice): number {
    if (attacker === defender) return this.ELEMENT_MODIFIERS.NEUTRAL
    
    // AGC: Define the elemental advantage cycle
    const advantages = {
      WATER: 'FIRE',    // Water beats Fire
      FIRE: 'ICE',      // Fire beats Ice  
      ICE: 'WIND',      // Ice beats Wind
      WIND: 'WATER'     // Wind beats Water
    }
    
    // AGC: Check if attacker has advantage
    if (advantages[attacker] === defender) {
      return this.ELEMENT_MODIFIERS.ADVANTAGE
    }
    
    // AGC: Check if defender has advantage (attacker disadvantage)
    if (advantages[defender] === attacker) {
      return this.ELEMENT_MODIFIERS.DISADVANTAGE
    }
    
    // AGC: All other combinations are neutral (draw)
    return this.ELEMENT_MODIFIERS.NEUTRAL
  }

  /**
   * AGC: Generates deterministic RNG value from seed and parameters
   * AGC: Formula: U = uint256(keccak256(label, seed, matchId, turn, player)) % 1_000_000
   */
  static generateRNG(label: string, seed: string, matchId: string, turn: number, player: string): number {
    // AGC: Improved deterministic hash for better randomness distribution
    const input = `${label}-${seed}-${matchId}-${turn}-${player}`
    
    // AGC: Use multiple hash rounds for better distribution
    let hash1 = 0
    let hash2 = 0
    
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i)
      hash1 = ((hash1 << 5) - hash1 + char) & 0xffffffff
      hash2 = ((hash2 << 7) - hash2 + char * 31) & 0xffffffff
    }
    
    // AGC: Combine hashes and add player-specific multiplier
    const playerMultiplier = player === 'A' ? 17 : 23
    const combinedHash = (hash1 ^ hash2) * playerMultiplier
    
    return Math.abs(combinedHash) % 1_000_000
  }

  /**
   * AGC: Determines rarity based on RNG roll and thresholds
   * AGC: Rarity buckets (ppm): Diamond<1000; Gold<11000; Silver<61000; Bronze<161000
   */
  static determineRarity(rngValue: number): RarityType {
    if (rngValue < this.RARITY_THRESHOLDS.DIAMOND) return 'DIAMOND'
    if (rngValue < this.RARITY_THRESHOLDS.GOLD) return 'GOLD'
    if (rngValue < this.RARITY_THRESHOLDS.SILVER) return 'SILVER'
    if (rngValue < this.RARITY_THRESHOLDS.BRONZE) return 'BRONZE'
    return 'NONE'
  }

  /**
   * AGC: OLD BUFF SYSTEM (COMMENTED OUT - 2025-10-30)
   * AGC: Single buff = 50% chance every turn
   * AGC: Two buffs = Position 1: 50%, Position 2: 80% decreasing by 5% each turn (min 50%)
   * AGC: FROST is one-time use only - remove from available buffs after first activation
   */
  /*
  static determineBuffProc_OLD(rngValue: number, availableBuffs: BuffType[], turn: number): BuffType | null {
    if (availableBuffs.length === 0) return null
    
    if (availableBuffs.length === 1) {
      // AGC: Single buff = 50% chance
      return rngValue < 500000 ? availableBuffs[0] : null
    }
    
    if (availableBuffs.length === 2) {
      // AGC: Position 1 = 50% chance
      const position1Chance = 500000 // 50%
      if (rngValue < position1Chance) {
        return availableBuffs[0]
      }
      
      // AGC: Position 2 = 80% decreasing by 5% each turn (min 50%)
      const position2BaseRate = 800000 // 80%
      const position2Reduction = Math.min(turn - 1, 6) * 50000 // 5% per turn, max 6 turns
      const position2FinalRate = Math.max(position2BaseRate - position2Reduction, 500000) // Min 50%
      
      // AGC: Check if position 2 procs (only if position 1 didn't proc)
      if (rngValue < position2FinalRate) {
        return availableBuffs[1]
      }
    }
    
    return null
  }
  */

  /**
   * AGC: NEW BUFF SYSTEM (2025-10-30) - Buff-specific proc rates
   * AGC: Single buff uses base proc rate (30%, 40%, 50%, 60%)
   * AGC: Two buffs:
   * AGC:   - Position 1: Uses position1 proc rate (20%, 40%, 50%, 60%)
   * AGC:   - Position 2 if Position 1 FAILS: Uses boosted rate (40%, 60%, 80%, 90%)
   * AGC:   - Position 2 if Position 1 SUCCEEDS: Uses reduced rate (10%, 20%, 25%, 30%)
   * AGC: Both positions use SEPARATE RNG values (allows both to activate)
   * AGC: FROST is one-time use only - remove from available buffs after first activation
   */
  
  // AGC: Buff-specific proc rates (parts per million)
  private static readonly BUFF_PROC_RATES = {
    'FROST': {
      single: 300000,      // 30% (1 buff)
      position1: 200000,   // 20% (2 buffs, pos 1)
      position2_boosted: 400000,   // 40% (pos 1 failed)
      position2_reduced: 100000    // 10% (pos 1 succeeded)
    },
    'SHIELD': {
      single: 400000,      // 40%
      position1: 400000,   // 40%
      position2_boosted: 600000,   // 60%
      position2_reduced: 200000    // 20%
    },
    'PIERCE': {
      single: 500000,      // 50%
      position1: 500000,   // 50%
      position2_boosted: 800000,   // 80%
      position2_reduced: 250000    // 25%
    },
    'HASTE': {
      single: 600000,      // 60%
      position1: 600000,   // 60%
      position2_boosted: 900000,   // 90%
      position2_reduced: 300000    // 30%
    }
  } as const

  /**
   * AGC: Determine buff proc with new buff-specific rates
   * AGC: Returns array of activated buffs (allows multiple buffs to activate)
   */
  static determineBuffProc(rng1: number, availableBuffs: BuffType[], turn: number, rng2?: number): BuffType[] {
    if (availableBuffs.length === 0) return []
    
    const activated: BuffType[] = []
    
    if (availableBuffs.length === 1) {
      // AGC: Single buff uses base proc rate
      const buffType = availableBuffs[0]
      const threshold = this.BUFF_PROC_RATES[buffType]?.single || 500000 // Default 50% if unknown
      
      if (rng1 < threshold) {
        activated.push(buffType)
      }
      
      return activated
    }
    
    if (availableBuffs.length === 2) {
      // AGC: Position 1 check
      const buff1Type = availableBuffs[0]
      const position1Threshold = this.BUFF_PROC_RATES[buff1Type]?.position1 || 500000
      
      const position1Activated = rng1 < position1Threshold
      
      if (position1Activated) {
        activated.push(buff1Type)
      }
      
      // AGC: Position 2 check (SEPARATE RNG, boosted/reduced based on Position 1)
      const buff2Type = availableBuffs[1]
      const position2Threshold = position1Activated 
        ? (this.BUFF_PROC_RATES[buff2Type]?.position2_reduced || 250000)  // Reduced if pos1 succeeded
        : (this.BUFF_PROC_RATES[buff2Type]?.position2_boosted || 800000)  // Boosted if pos1 failed
      
      // AGC: Use rng2 if provided, otherwise use rng1 (backwards compatibility)
      const actualRng2 = rng2 !== undefined ? rng2 : rng1
      
      if (actualRng2 < position2Threshold) {
        activated.push(buff2Type)
      }
    }
    
    return activated
  }

  /**
   * AGC: Calculates defense multiplier based on defender level
   * AGC: Formula: DefMul = 1 - (0.01 * defenderLevel / 2)
   */
  static calculateDefenseMultiplier(defenderLevel: number): number {
    return 1 - (0.01 * defenderLevel / 2)
  }

  /**
   * AGC: Calculates level scaling multiplier
   * AGC: Formula: LevelMul = 1 + (0.01 * attackerLevel)
   */
  static calculateLevelMultiplier(attackerLevel: number): number {
    return 1 + (0.01 * attackerLevel)
  }

  /**
   * AGC: Calculate experience required for a specific level
   * AGC: MUST MATCH PlayerXP.sol contract formula!
   * AGC: Formula: XP = 100 * (level - 1)^2
   * AGC: Level 1 = 0 XP, Level 2 = 100 XP, Level 3 = 400 XP, Level 4 = 900 XP
   */
  static getExperienceForLevel(level: number): number {
    if (level <= 1) return 0
    return 100 * Math.pow(level - 1, 2)
  }

  /**
   * AGC: Calculate level from total experience
   * AGC: MUST MATCH PlayerXP.sol contract formula!
   * AGC: Formula: Level = sqrt(totalXP / 100) + 1
   */
  static getLevelFromExperience(totalExperience: number): number {
    if (totalExperience <= 0) return 1
    
    // AGC: Level = sqrt(totalXP / 100) + 1 (same as blockchain)
    const level = Math.floor(Math.sqrt(totalExperience / 100)) + 1
    
    return Math.min(level, 30) // AGC: Cap at level 30
  }

  /**
   * AGC: Calculate player stats based on level and experience
   */
  static calculatePlayerStats(totalExperience: number): PlayerStats {
    const level = this.getLevelFromExperience(totalExperience)
    const currentLevelXP = this.getExperienceForLevel(level)
    
    // AGC: Handle max level (30) - no more experience needed
    let nextLevelXP, experienceToNext
    if (level >= 30) {
      nextLevelXP = currentLevelXP
      experienceToNext = 0 // AGC: No more experience needed at max level
    } else {
      nextLevelXP = this.getExperienceForLevel(level + 1)
      experienceToNext = nextLevelXP - totalExperience
    }
    
    // AGC: Calculate stats based on level with balanced scaling (level already capped at 30)
    const attack = Math.floor(10 + (level * 2.5)) // Base 10, +2.5 per level (max 85 at level 30)
    const defense = Math.floor(5 + (level * 1.5)) // Base 5, +1.5 per level (max 50 at level 30)
    const health = Math.floor(100 + Math.min(level * 2, 50)) // Base 100, +2 per level, max +50 at level 25 (150 total)
    
    return {
      level,
      experience: totalExperience - currentLevelXP,
      experienceToNext,
      totalExperience,
      attack,
      defense,
      health
    }
  }

  /**
   * AGC: Calculate experience gained from battle outcome
   * AGC: Updated to support different XP rates for PvE vs P2P
   */
  static calculateExperienceGain(
    outcome: 'WIN' | 'DRAW' | 'LOSE',
    opponentLevel: number,
    playerLevel: number,
    battleType: 'PVE' | 'PVP' = 'PVP' // AGC: Default to P2P (higher XP)
  ): number {
    // AGC: No experience gained if player is at max level (30)
    if (playerLevel >= 30) {
      return 0
    }
    
    // AGC: Base XP based on outcome (matches backend BattleStateManager.calculatePvPXP)
    let baseXp = 0
    switch (outcome) {
      case 'WIN':
        baseXp = 50
        break
      case 'DRAW':
        baseXp = 25
        break
      case 'LOSE':
        baseXp = 15
        break
    }
    
    // AGC: Level difference multiplier (same as backend)
    const levelDiff = opponentLevel - playerLevel
    let diffMultiplier = 1.0

    if (levelDiff > 0) {
      diffMultiplier = 1.0 + (levelDiff * 0.1) // AGC: +10% per level higher
    } else if (levelDiff < 0) {
      diffMultiplier = Math.max(0.5, 1.0 + (levelDiff * 0.05)) // AGC: -5% per level lower (min 50%)
    }

    // AGC: Core PVP XP = floor(base * multiplier) to match backend
    const pvpXp = Math.floor(baseXp * diffMultiplier)

    if (battleType === 'PVE') {
      // AGC: Hybrid PvE grants 50% of PVP XP, rounded like backend does
      const halved = Math.round(pvpXp / 2)
      if (outcome === 'LOSE') {
        return Math.max(0, halved)
      }
      return Math.max(1, halved)
    }
    
    return Math.max(outcome === 'LOSE' ? 0 : 1, pvpXp)
  }

  /**
   * AGC: Calculates raw damage before defense application
   * AGC: Includes base damage, level scaling, element bonus, rarity bonus, and haste
   */
  static calculateRawDamage(
    origamiOutcome: 'WIN' | 'DRAW' | 'LOSE',
    attackerLevel: number,
    elementAdvantage: number,
    rarity: RarityType,
    hasHaste: boolean
  ): number {
    // AGC: Start with base damage based on origami outcome
    let damage = this.BASE_DAMAGE[origamiOutcome]
    
    // AGC: Apply level scaling
    damage *= this.calculateLevelMultiplier(attackerLevel)
    
    // AGC: Apply element advantage/disadvantage
    damage += elementAdvantage
    
    // AGC: Apply rarity bonus
    damage += this.RARITY_BONUSES[rarity]
    
    // AGC: Apply haste flat bonus (+5 before defense)
    if (hasHaste) {
      damage += 5
    }
    
    return damage
  }

  /**
   * AGC: Applies pierce effect to defense multiplier
   * AGC: Pierce reduces opponent's defense by 50%
   */
  static applyPierceEffect(defenseMultiplier: number, _defenderLevel: number): number {
    // AGC: Pierce reduces defense effectiveness by 50%
    // AGC: Example: If defense mult is 0.95 (blocks 5% damage), Pierce makes it 0.975 (blocks only 2.5%)
    return 1 - ((1 - defenseMultiplier) * 0.5)
  }

  /**
   * AGC: Applies shield effect (25% damage reduction)
   * AGC: Shield multiplies final damage by 0.75
   */
  static applyShieldEffect(damage: number): number {
    return damage * 0.75
  }

  /**
   * AGC: Main battle resolution function - calculates turn results
   * AGC: Implements complete damage formula from specification
   */
  static resolveTurn(
    playerA: Player,
    playerB: Player,
    seed: string,
    matchId: string,
    turn: number
  ): BattleResult {
    if (!playerA.choice || !playerB.choice) {
      throw new Error('Both players must make choices before resolving turn')
    }

    // AGC: Generate RNG values for both players
    const rngA_rarity = this.generateRNG('rarity', seed, matchId, turn, 'A')
    const rngB_rarity = this.generateRNG('rarity', seed, matchId, turn, 'B')
    const rngA_buff = this.generateRNG('buff', seed, matchId, turn, 'A')
    const rngB_buff = this.generateRNG('buff', seed, matchId, turn, 'B')
    const rngA_buffSecondary = this.generateRNG('buff_secondary', seed, matchId, turn, 'A')
    const rngB_buffSecondary = this.generateRNG('buff_secondary', seed, matchId, turn, 'B')

    // AGC: Determine rarities
    const rarityA = this.determineRarity(rngA_rarity)
    const rarityB = this.determineRarity(rngB_rarity)

    // AGC: Determine buff procs (pass turn number for new system)
    // AGC: FROST is one-time use - filter it out if already used
    const availableBuffsA = playerA.usedFrost 
      ? playerA.buffs.filter(b => b !== 'FROST') 
      : playerA.buffs
    const availableBuffsB = playerB.usedFrost 
      ? playerB.buffs.filter(b => b !== 'FROST') 
      : playerB.buffs
    
    const buffsA = this.determineBuffProc(rngA_buff, availableBuffsA, turn, rngA_buffSecondary)
    const buffsB = this.determineBuffProc(rngB_buff, availableBuffsB, turn, rngB_buffSecondary)

    const hasBuffA = (buff: BuffType) => buffsA.includes(buff)
    const hasBuffB = (buff: BuffType) => buffsB.includes(buff)
    
    // AGC: Mark Frost as used if it activated this turn (one-time use only)
    if (hasBuffA('FROST') && !playerA.usedFrost) {
      playerA.usedFrost = true
    }
    if (hasBuffB('FROST') && !playerB.usedFrost) {
      playerB.usedFrost = true
    }

    // AGC: Calculate origami outcomes
    const outcomeA = this.determineOrigamiOutcome(playerA.choice.origami, playerB.choice.origami)
    const outcomeB = this.determineOrigamiOutcome(playerB.choice.origami, playerA.choice.origami)

    // AGC: Calculate element advantages
    const elementAdvA = this.determineElementAdvantage(playerA.choice.element, playerB.choice.element)
    const elementAdvB = this.determineElementAdvantage(playerB.choice.element, playerA.choice.element)

    // AGC: Calculate raw damage and track breakdown for both players
    const breakdownA: DamageBreakdown = {
      baseDamage: this.BASE_DAMAGE[outcomeA],
      baseName: outcomeA,
      levelBonus: playerA.level,
      elementBonus: elementAdvA,
      elementName: elementAdvA > 0 ? 'ADVANTAGE' : elementAdvA < 0 ? 'DISADVANTAGE' : 'NEUTRAL',
      rarityBonus: this.RARITY_BONUSES[rarityA],
      rarityName: rarityA,
      hasteBonus: hasBuffA('HASTE') ? 5 : 0,
      rawDamage: 0, // Will calculate below
      defense: 0, // Will calculate below
      afterDefense: 0, // Will calculate below
      finalDamage: 0, // Will calculate below
      activatedBuffs: [...buffsA]
    }

    const breakdownB: DamageBreakdown = {
      baseDamage: this.BASE_DAMAGE[outcomeB],
      baseName: outcomeB,
      levelBonus: playerB.level,
      elementBonus: elementAdvB,
      elementName: elementAdvB > 0 ? 'ADVANTAGE' : elementAdvB < 0 ? 'DISADVANTAGE' : 'NEUTRAL',
      rarityBonus: this.RARITY_BONUSES[rarityB],
      rarityName: rarityB,
      hasteBonus: hasBuffB('HASTE') ? 5 : 0,
      rawDamage: 0,
      defense: 0,
      afterDefense: 0,
      finalDamage: 0,
      activatedBuffs: [...buffsB]
    }

    // AGC: Calculate raw damage for both players
    let damageA = this.calculateRawDamage(
      outcomeA,
      playerA.level,
      elementAdvA,
      rarityA,
      hasBuffA('HASTE')
    )

    let damageB = this.calculateRawDamage(
      outcomeB,
      playerB.level,
      elementAdvB,
      rarityB,
      hasBuffB('HASTE')
    )

    breakdownA.rawDamage = damageA
    breakdownB.rawDamage = damageB

    // AGC: Apply defense multipliers
    let defenseMultA = this.calculateDefenseMultiplier(playerA.level)
    let defenseMultB = this.calculateDefenseMultiplier(playerB.level)

    breakdownA.defense = defenseMultB
    breakdownB.defense = defenseMultA

    // AGC: Apply pierce effects if present
    if (hasBuffB('PIERCE')) {
      const originalDefA = defenseMultA
      defenseMultA = this.applyPierceEffect(defenseMultA, playerA.level)
      breakdownB.pierceReduction = (originalDefA - defenseMultA) * damageB
    }
    if (hasBuffA('PIERCE')) {
      const originalDefB = defenseMultB
      defenseMultB = this.applyPierceEffect(defenseMultB, playerB.level)
      breakdownA.pierceReduction = (originalDefB - defenseMultB) * damageA
    }

    // AGC: Apply defense to damage
    damageA *= defenseMultB
    damageB *= defenseMultA

    breakdownA.afterDefense = damageA
    breakdownB.afterDefense = damageB

    // AGC: Apply frost effects if present (freeze opponent = 0 damage)
    if (hasBuffA('FROST')) {
      breakdownB.frostReduction = damageB
      damageB = 0 // Player A freezes Player B - Player B deals no damage this turn
    }
    if (hasBuffB('FROST')) {
      breakdownA.frostReduction = damageA
      damageA = 0 // Player B freezes Player A - Player A deals no damage this turn
    }

    // AGC: Apply shield effects if present
    if (hasBuffA('SHIELD')) {
      const originalDamage = damageB
      damageB = this.applyShieldEffect(damageB)
      breakdownB.shieldReduction = originalDamage - damageB
    }
    if (hasBuffB('SHIELD')) {
      const originalDamage = damageA
      damageA = this.applyShieldEffect(damageA)
      breakdownA.shieldReduction = originalDamage - damageA
    }

    // AGC: Round damage to integers as per specification
    const finalDamageA = Math.round(damageA)
    const finalDamageB = Math.round(damageB)

    breakdownA.finalDamage = finalDamageA
    breakdownB.finalDamage = finalDamageB

    // AGC: Update energy points
    const newEpA = playerA.ep - finalDamageB
    const newEpB = playerB.ep - finalDamageA

    // AGC: Determine winner
    let winner: 'A' | 'B' | 'DRAW' | undefined
    if (newEpA <= 0 && newEpB <= 0) {
      winner = 'DRAW'
    } else if (newEpA <= 0) {
      winner = 'B'
    } else if (newEpB <= 0) {
      winner = 'A'
    }

    return {
      playerA_damage: finalDamageA,
      playerB_damage: finalDamageB,
      playerA_ep: Math.max(0, newEpA),
      playerB_ep: Math.max(0, newEpB),
      winner,
      rng_seed: seed,
      playerA_rarity: rarityA,
      playerB_rarity: rarityB,
      playerA_buff: buffsA[0] ?? null,
      playerB_buff: buffsB[0] ?? null,
      playerA_buffs: buffsA,
      playerB_buffs: buffsB,
      playerA_breakdown: breakdownA,
      playerB_breakdown: breakdownB
    }
  }

  /**
   * AGC: Validates battle result against golden test vectors
   * AGC: Used for testing and verification of game engine accuracy
   */
  static validateAgainstGoldenVector(testCase: any): boolean {
    try {
      const playerA: Player = {
        id: 'A',
        level: testCase.players.A.level,
        ep: 100, // Standard starting EP
        experience: 0, // AGC: Default experience for test
        choice: testCase.choices.A,
        buffs: testCase.rng.A_buff ? [testCase.rng.A_buff] : []
      }

      const playerB: Player = {
        id: 'B',
        level: testCase.players.B.level,
        ep: 100, // Standard starting EP
        experience: 0, // AGC: Default experience for test
        choice: testCase.choices.B,
        buffs: testCase.rng.B_buff ? [testCase.rng.B_buff] : []
      }

      const result = this.resolveTurn(playerA, playerB, testCase.seed_hint || 'test', 'test', testCase.turn || 1)
      
      // AGC: Compare with expected results
      return (
        result.playerA_damage === testCase.expected.A_damage &&
        result.playerB_damage === testCase.expected.B_damage
      )
    } catch (error) {
      console.error('Validation error:', error)
      return false
    }
  }
}
